import { zodResolver } from '@hookform/resolvers/zod'
import { CreditCard, Loader2 } from 'lucide-react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import { DateField } from '@/components/ui/date-field'
import { Input } from '@/components/ui/input'
import { Modal } from '@/components/ui/modal'
import { NumberInput } from '@/components/ui/number-input'
import { FormSelect } from '@/components/ui/select'
import { fmtDate, fmtMoney } from '../../shared/format'
import { usePayObligationItem } from '../api'
import { PAYMENT_CHANNELS, type ObligationItem } from '../types'
import { toastError, useFinanceLookups } from '../ui'
import { Field, FormSection, ReadonlyValue } from './FormBits'

type Props = { item: ObligationItem | null; onClose: () => void }

/** Vade kalemini öder/tahsil eder: gelir-gider kaydı ve kasa/banka hareketi sunucuda birlikte oluşur. */
export function ObligationPayModal({ item, onClose }: Props) {
  return item ? <PayForm key={item.id} item={item} onClose={onClose} /> : null
}

function PayForm({ item, onClose }: { item: ObligationItem; onClose: () => void }) {
  const { t } = useTranslation()
  const pay = usePayObligationItem()
  const { accounts, accountDetail } = useFinanceLookups()
  const incoming = item.direction === 'in'

  const schema = z.object({
    accountId: z.string().min(1, t('finance.pay.form.accountRequired')),
    paidOn: z.string().min(1, t('finance.form.dateRequired')),
    amount: z
      .string()
      .regex(/^\d+(\.\d{1,2})?$/, t('finance.form.amountInvalid'))
      .refine((v) => Number(v) > 0, t('finance.form.amountInvalid')),
    channel: z.enum(PAYMENT_CHANNELS),
    docNo: z.string().trim().max(60),
    note: z.string().trim().max(240),
  })
  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      accountId: item.accountId,
      paidOn: new Date().toISOString().slice(0, 10),
      amount: item.amount,
      channel: 'transfer' as const,
      docNo: '',
      note: '',
    },
  })
  const { errors } = form.formState

  const onSubmit = form.handleSubmit(async (v) => {
    try {
      const res = await pay.mutateAsync({ id: item.id, input: v })
      if (res.pendingApproval)
        toast.info(t('finance.paymentRequests.sent'), { description: t('finance.paymentRequests.sentHint') })
      else toast.success(t(incoming ? 'finance.pay.collected' : 'finance.pay.paid'))
      onClose()
    } catch (e) {
      toastError(e)
    }
  })

  return (
    <Modal
      open
      onOpenChange={(v) => !v && onClose()}
      dismissible={!pay.isPending}
      icon={<CreditCard />}
      title={t(incoming ? 'finance.obligations.pay.titleIn' : 'finance.obligations.pay.titleOut')}
      description={t('finance.obligations.pay.hint')}
      onSubmit={(e) => void onSubmit(e)}
      footer={
        <>
          <Button type="button" variant="cancel" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={pay.isPending}>
            {pay.isPending ? <Loader2 className="animate-spin" /> : null}
            {t(incoming ? 'finance.obligations.actions.collect' : 'finance.obligations.actions.pay')}
          </Button>
        </>
      }
    >
      <FormSection>
        <Field id="op-item" label={t('finance.obligations.pay.item')} full>
          <ReadonlyValue id="op-item">
            {item.title} · {item.period} · {t('finance.obligations.due')} {fmtDate(item.dueDate)} ·{' '}
            {fmtMoney(item.amount)}
          </ReadonlyValue>
        </Field>
        <Field
          id="op-amount"
          label={t(incoming ? 'finance.pay.form.amountIn' : 'finance.pay.form.amountOut')}
          error={errors.amount?.message}
          hint={t('finance.obligations.pay.amountHint')}
        >
          <Controller
            control={form.control}
            name="amount"
            render={({ field }) => (
              <NumberInput
                id="op-amount"
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                minDecimalsOnBlur={2}
                suffix="₺"
                aria-invalid={!!errors.amount}
              />
            )}
          />
        </Field>
        <Field id="op-date" label={t('finance.pay.date')}>
          <Controller
            control={form.control}
            name="paidOn"
            render={({ field }) => (
              <DateField id="op-date" value={field.value} onChange={field.onChange} clearable={false} />
            )}
          />
        </Field>
        <Field id="op-account" label={t('finance.pay.account')} error={errors.accountId?.message}>
          <FormSelect
            id="op-account"
            control={form.control}
            name="accountId"
            placeholder={t('common.select')}
            options={accounts
              .filter((a) => a.active)
              .map((a) => ({
                value: a.id,
                label: a.name,
                description: [accountDetail(a.id), fmtMoney(a.balance)].filter(Boolean).join(' · '),
              }))}
          />
        </Field>
        <Field id="op-channel" label={t('finance.pay.channel')}>
          <FormSelect
            id="op-channel"
            control={form.control}
            name="channel"
            options={PAYMENT_CHANNELS.map((c) => ({ value: c, label: t(`finance.channel.${c}`) }))}
          />
        </Field>
        <Field id="op-doc" label={t('finance.obligations.pay.docNo')}>
          <Input id="op-doc" maxLength={60} {...form.register('docNo')} />
        </Field>
        <Field id="op-note" label={t('finance.pay.note')}>
          <Input id="op-note" maxLength={240} {...form.register('note')} />
        </Field>
      </FormSection>
    </Modal>
  )
}
